import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../../api";
import usePageMeta from "../../hooks/usePageMeta";
import StoreSelector from "../stockRecords/StoreSelector";
import FilterLabel from "./FilterLabel";
import KpiCards from "./KpiCards";
import SalesTrendChart from "./SalesTrendChart";
import DepartmentDonut from "./DepartmentDonut";
import TopMoversChart from "./TopMoversChart";
import AlertsPanel from "./AlertsPanel";
import ForecastPanel from "./ForecastPanel";
import ReorderTable from "./ReorderTable";
import { buildParams, MONTH_OPTIONS } from "./analyticsUtils";

const now = new Date();
const YEARS = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
const LOOKBACK_OPTIONS = [6, 9, 12, 18, 24];
const SERVICE_LEVELS = [
    { value: 0.9, label: "90%" },
    { value: 0.95, label: "95%" },
    { value: 0.98, label: "98%" },
];

const AnalyticsDashboard = ({ embedded = false, storeIdOverride }) => {
    const { user } = useSelector((s) => s.verifyEmailStore);
    const isAdmin = user?.role === "Admin";

    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [months, setMonths] = useState(12);
    const [leadDays, setLeadDays] = useState(7);
    const [serviceLevel, setServiceLevel] = useState(0.95);
    const [storeId, setStoreId] = useState(storeIdOverride ? String(storeIdOverride) : "");

    const [overview, setOverview] = useState(null);
    const [trend, setTrend] = useState(null);
    const [topMovers, setTopMovers] = useState([]);
    const [bottomMovers, setBottomMovers] = useState([]);
    const [departments, setDepartments] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [loading, setLoading] = useState(false);

    usePageMeta("analytics");

    useEffect(() => {
        if (storeIdOverride) setStoreId(String(storeIdOverride));
    }, [storeIdOverride]);

    const effectiveStoreId = isAdmin ? storeId : "";
    const ready = !isAdmin || !!effectiveStoreId;

    const paramsBase = useMemo(
        () => ({
            year,
            month,
            months,
            leadDays,
            serviceLevel,
            store_id: isAdmin ? effectiveStoreId : undefined,
        }),
        [year, month, months, leadDays, serviceLevel, isAdmin, effectiveStoreId]
    );

    const fetchAll = useCallback(async () => {
        if (!ready) return;
        setLoading(true);
        try {
            const qs = (extra) => buildParams(paramsBase, extra).toString();
            const [ov, tr, top, bottom, dep, al] = await Promise.all([
                api.get(`/api/v1/analytics/overview?${qs()}`),
                api.get(`/api/v1/analytics/sales-trend?${qs()}`),
                api.get(`/api/v1/analytics/top-movers?${qs({ direction: "top", limit: 8 })}`),
                api.get(`/api/v1/analytics/top-movers?${qs({ direction: "bottom", limit: 8 })}`),
                api.get(`/api/v1/analytics/department-breakdown?${qs()}`),
                api.get(`/api/v1/analytics/alerts?${qs()}`),
            ]);
            if (ov.data.success) setOverview(ov.data);
            if (tr.data.success) setTrend(tr.data);
            if (top.data.success) setTopMovers(top.data.rows || []);
            if (bottom.data.success) setBottomMovers(bottom.data.rows || []);
            if (dep.data.success) setDepartments(dep.data);
            if (al.data.success) setAlerts(al.data);
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to load analytics");
        } finally {
            setLoading(false);
        }
    }, [paramsBase, ready]);

    useEffect(() => {
        const timer = setTimeout(fetchAll, 250);
        return () => clearTimeout(timer);
    }, [fetchAll]);

    return (
        <Fragment>
            {!embedded && (
                <section className="pl-[1.5vmax] pr-[2.5vmax]">
                    <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                        <h2 className="text-[1.3vmax] font-bold">Analytics &amp; Forecasting</h2>
                        <div className="flex-center gap-[0.8vmax] text-[1vmax]">
                            <Link to="/">Dashboard</Link>
                            <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black" />
                            <span className="text-slate-400">Analytics</span>
                        </div>
                    </div>
                </section>
            )}

            <section className={`${embedded ? "" : "pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax]"} flex flex-col gap-4`}>
                {/* Filter bar */}
                <div className="bg-white rounded-2xl shadow-1 p-4 flex flex-wrap items-end gap-3">
                    {isAdmin && !storeIdOverride && (
                        <StoreSelector storeId={storeId} setStoreId={setStoreId} user={user} />
                    )}
                    <div className="flex flex-col gap-1">
                        <FilterLabel
                            label="Month"
                            hint="The reporting period. Every number and chart here is calculated for this month and compared against the month before it."
                        />
                        <select
                            value={month}
                            onChange={(e) => setMonth(Number(e.target.value))}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                        >
                            {MONTH_OPTIONS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <FilterLabel
                            label="Year"
                            hint="The year for the month you selected above."
                        />
                        <select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                        >
                            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <FilterLabel
                            label="Lookback"
                            hint="How much past sales history to study when predicting demand. More months usually means smarter, steadier forecasts."
                        />
                        <select
                            value={months}
                            onChange={(e) => setMonths(Number(e.target.value))}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                        >
                            {LOOKBACK_OPTIONS.map((m) => <option key={m} value={m}>{m} months</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <FilterLabel
                            label="Lead time"
                            hint="How many days your supplier takes to deliver after you place an order. Longer delivery times mean you must reorder earlier to avoid running out."
                        />
                        <select
                            value={leadDays}
                            onChange={(e) => setLeadDays(Number(e.target.value))}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                        >
                            {[3, 7, 14, 30].map((d) => <option key={d} value={d}>{d} days</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <FilterLabel
                            label="Service level"
                            hint="How sure you want to be that you won't run out of stock. Higher % keeps extra 'just in case' stock — fewer stockouts, but more cash tied up in inventory."
                        />
                        <select
                            value={serviceLevel}
                            onChange={(e) => setServiceLevel(Number(e.target.value))}
                            className="border border-slate-200 rounded-lg px-3 py-2 text-[13px] outline-[var(--color-2)]"
                        >
                            {SERVICE_LEVELS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                    <button
                        onClick={fetchAll}
                        className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-semibold hover:bg-slate-200"
                    >
                        <i className={`fa-solid fa-rotate ${loading ? "fa-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {isAdmin && !ready ? (
                    <div className="bg-white rounded-2xl shadow-1 p-10 text-center text-slate-400 text-[14px]">
                        Select a store to view its analytics.
                    </div>
                ) : (
                    <>
                        <KpiCards overview={overview} alerts={alerts} />

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="lg:col-span-2">
                                <SalesTrendChart trend={trend} />
                            </div>
                            <DepartmentDonut salesDepartments={departments?.salesDepartments} />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <TopMoversChart
                                topMovers={topMovers}
                                bottomMovers={bottomMovers}
                                loading={loading}
                            />
                            <AlertsPanel alerts={alerts} loading={loading} />
                        </div>

                        <ForecastPanel paramsBase={paramsBase} />

                        <ReorderTable paramsBase={paramsBase} periodLabel={overview?.period?.label} />
                    </>
                )}
            </section>

            {!embedded && <div className="pb-[4vmax]" />}
        </Fragment>
    );
};

export default AnalyticsDashboard;
